import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { PageHeader, Button } from "@/components/ui/PageHeader";
import {
  InputField,
  TextAreaField,
  SelectField,
  TagsField,
} from "@/components/ui/FormFields";
import { useAuth } from "@/context/AuthContext";
import {
  createProvider,
  updateProvider,
  getProvider,
  getProviderCategories,
  type ProviderInput,
} from "@/api/providers";

// A blank listing — mirrors createProviderSchema's optional/required split.
const EMPTY: ProviderInput = {
  name: "",
  categoryCode: "",
  subcategoryCode: "",
  description: "",
  phone: "",
  email: "",
  website: "",
  address: "",
  city: "",
  state: "",
  zipCode: "",
  originState: "",
  languages: [],
  skills: [],
};

/**
 * Become a provider (create) OR manage your own listing (edit).
 *
 * One page, two modes keyed off the `:idOrSlug` route param:
 *   /lab/providers/new              → create
 *   /lab/providers/:idOrSlug/edit   → edit (owner-gated)
 *
 * The backend enforces ownership on PUT (added-by or claimed-by), so the
 * Lab gate is a courtesy — it hides the form for a non-owner rather than
 * letting them submit into a guaranteed 403.
 */
export default function ProviderManage() {
  const { idOrSlug } = useParams<{ idOrSlug?: string }>();
  const isEdit = !!idOrSlug;
  const navigate = useNavigate();
  const { user } = useAuth();

  const [form, setForm] = useState<ProviderInput>(EMPTY);
  const [providerId, setProviderId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const { data: cats } = useQuery({
    queryKey: ["provider-categories"],
    queryFn: getProviderCategories,
  });

  // Edit mode: pull the existing listing and prefill.
  const {
    data: existing,
    isLoading: loadingExisting,
    error: loadError,
  } = useQuery({
    queryKey: ["provider", idOrSlug],
    queryFn: () => getProvider(idOrSlug!),
    enabled: isEdit,
  });

  useEffect(() => {
    const p = existing?.data;
    if (!p) return;
    setProviderId(p.id);
    setForm({
      name: p.name,
      categoryCode: p.category.code,
      subcategoryCode: p.subcategory?.code ?? "",
      description: p.description ?? "",
      phone: p.phone ?? "",
      email: p.email ?? "",
      website: p.website ?? "",
      address: p.address ?? "",
      city: p.city,
      state: p.state,
      zipCode: p.zipCode ?? "",
      originState: p.originState ?? "",
      languages: p.languages ?? [],
      skills: p.skills ?? [],
    });
  }, [existing]);

  const categoryOptions = [
    { value: "", label: "Choose a category…" },
    ...(cats?.data ?? []).map((c) => ({ value: c.code, label: c.name })),
  ];

  const selectedCat = (cats?.data ?? []).find(
    (c) => c.code === form.categoryCode,
  );
  const suggestedSkills = selectedCat?.suggestedSkills ?? [];
  const subcategoryOptions = useMemo(
    () => [
      { value: "", label: selectedCat ? "General" : "Pick a category first" },
      ...(selectedCat?.subcategories ?? []).map((s) => ({
        value: s.code,
        label: s.name,
      })),
    ],
    [selectedCat],
  );

  // Owner gate for edit mode: only the person who added the listing sees
  // the form. (A claimer is also allowed server-side, but the detail
  // payload doesn't expose claimedBy, so we gate on added-by here.)
  const ownsListing =
    !isEdit || (existing?.data?.addedBy?.id != null && existing.data.addedBy.id === user?.id);

  function set<K extends keyof ProviderInput>(key: K, value: ProviderInput[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  // Add a suggested skill unless it's already on the list.
  function addSkill(skill: string) {
    setForm((f) =>
      f.skills?.includes(skill) ? f : { ...f, skills: [...(f.skills ?? []), skill] },
    );
  }

  function validate(): boolean {
    const errs: Record<string, string> = {};
    if (!form.name.trim()) errs.name = "Required";
    if (!form.categoryCode) errs.categoryCode = "Required";
    if (!form.city.trim()) errs.city = "Required";
    if (!form.state.trim()) errs.state = "Required";
    if (form.email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email))
      errs.email = "Enter a valid email";
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!validate()) return;
    setBusy(true);

    // Trim empties → omit optional fields rather than send "".
    const payload: ProviderInput = {
      name: form.name.trim(),
      categoryCode: form.categoryCode,
      subcategoryCode: form.subcategoryCode || undefined,
      description: form.description?.trim() || undefined,
      phone: form.phone?.trim() || undefined,
      email: form.email?.trim() || undefined,
      website: form.website?.trim() || undefined,
      address: form.address?.trim() || undefined,
      city: form.city.trim(),
      state: form.state.trim(),
      zipCode: form.zipCode?.trim() || undefined,
      originState: form.originState?.trim() || undefined,
      languages: form.languages?.length ? form.languages : undefined,
      skills: form.skills?.length ? form.skills : undefined,
    };

    try {
      const res =
        isEdit && providerId
          ? await updateProvider(providerId, payload)
          : await createProvider(payload);
      navigate(`/lab/providers/${res.data.slug}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong.");
    } finally {
      setBusy(false);
    }
  }

  if (isEdit && loadingExisting) {
    return <p className="text-sm text-gray-500">Loading listing…</p>;
  }

  if (isEdit && loadError) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-xl">
        Couldn't load this listing.
      </div>
    );
  }

  if (isEdit && !ownsListing) {
    return (
      <div className="space-y-4">
        <PageHeader
          title="Manage listing"
          breadcrumbs={[
            { label: "Dashboard", href: "/" },
            { label: "Providers", href: "/lab/providers" },
            { label: "Manage" },
          ]}
        />
        <div className="bg-amber-50 border border-amber-200 text-amber-800 text-sm px-4 py-3 rounded-xl">
          This listing belongs to someone else. Only the person who added it
          (or claimed it) can edit it.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl">
      <PageHeader
        title={isEdit ? "Edit your listing" : "List your service"}
        subtitle={
          isEdit
            ? "Keep your details current so the community can find and trust you."
            : "Add yourself to the directory. Neighbours discover you through vouches — the more complete your profile, the more you'll be found."
        }
        breadcrumbs={[
          { label: "Dashboard", href: "/" },
          { label: "Providers", href: "/lab/providers" },
          { label: isEdit ? "Edit" : "New" },
        ]}
      />

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-xl">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* ── Basics ─────────────────────────────────────────── */}
        <section className="bg-white border border-gray-200 rounded-2xl p-5 space-y-4">
          <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
            The basics
          </h2>
          <InputField
            label="Name"
            required
            placeholder="e.g. Meera's Home Childcare"
            value={form.name}
            error={fieldErrors.name}
            onChange={(e) => set("name", e.target.value)}
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <SelectField
              label="Category"
              required
              value={form.categoryCode}
              error={fieldErrors.categoryCode}
              onChange={(e) => {
                // Changing category invalidates the old subcategory.
                set("categoryCode", e.target.value);
                set("subcategoryCode", "");
              }}
              options={categoryOptions}
            />
            <SelectField
              label="Type"
              hint="Optional — narrows what you do"
              value={form.subcategoryCode ?? ""}
              onChange={(e) => set("subcategoryCode", e.target.value)}
              options={subcategoryOptions}
            />
          </div>
          <TextAreaField
            label="About"
            rows={4}
            hint="What you offer, your experience, who you serve."
            placeholder="Gujarati-speaking nanny with 8 years' experience, first-aid certified, references available…"
            value={form.description ?? ""}
            onChange={(e) => set("description", e.target.value)}
          />
        </section>

        {/* ── Where ──────────────────────────────────────────── */}
        <section className="bg-white border border-gray-200 rounded-2xl p-5 space-y-4">
          <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
            Where you are
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <InputField
              label="City"
              required
              placeholder="Jersey City"
              value={form.city}
              error={fieldErrors.city}
              onChange={(e) => set("city", e.target.value)}
            />
            <InputField
              label="State / region"
              required
              placeholder="NJ"
              value={form.state}
              error={fieldErrors.state}
              onChange={(e) => set("state", e.target.value)}
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <InputField
              label="Indian roots"
              hint="Origin state — code or name (GJ, Gujarat). Helps people find someone from home."
              placeholder="Gujarat"
              value={form.originState ?? ""}
              onChange={(e) => set("originState", e.target.value)}
            />
            <InputField
              label="ZIP / postal code"
              placeholder="07302"
              value={form.zipCode ?? ""}
              onChange={(e) => set("zipCode", e.target.value)}
            />
          </div>
          <InputField
            label="Address"
            hint="Optional — shown on your profile"
            value={form.address ?? ""}
            onChange={(e) => set("address", e.target.value)}
          />
        </section>

        {/* ── Contact + languages ────────────────────────────── */}
        <section className="bg-white border border-gray-200 rounded-2xl p-5 space-y-4">
          <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
            How to reach you
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <InputField
              label="Phone"
              placeholder="+1 555 123 4567"
              value={form.phone ?? ""}
              onChange={(e) => set("phone", e.target.value)}
            />
            <InputField
              label="Email"
              type="email"
              placeholder="you@example.com"
              value={form.email ?? ""}
              error={fieldErrors.email}
              onChange={(e) => set("email", e.target.value)}
            />
          </div>
          <InputField
            label="Website"
            hint="Include https://"
            placeholder="https://…"
            value={form.website ?? ""}
            onChange={(e) => set("website", e.target.value)}
          />
          <TagsField
            label="Languages"
            hint="Press Enter after each — Gujarati, Hindi, English…"
            placeholder="Add a language"
            value={form.languages ?? []}
            onChange={(langs) => set("languages", langs)}
          />
        </section>

        {/* ── Skills ─────────────────────────────────────────── */}
        <section className="bg-white border border-gray-200 rounded-2xl p-5 space-y-4">
          <div>
            <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
              What you do
            </h2>
            <p className="text-xs text-gray-500 mt-1">
              These are how people filter the directory. Pick from the
              suggestions for your category, or add your own.
            </p>
          </div>

          {suggestedSkills.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {suggestedSkills.map((s) => {
                const on = form.skills?.includes(s);
                return (
                  <button
                    key={s}
                    type="button"
                    onClick={() => addSkill(s)}
                    disabled={on}
                    className={
                      on
                        ? "px-3 py-1.5 rounded-full text-xs border border-primary-200 bg-primary-50 text-primary-700 cursor-default"
                        : "px-3 py-1.5 rounded-full text-xs border border-gray-200 text-gray-700 hover:border-primary-300 hover:bg-primary-50 transition-colors"
                    }
                  >
                    {on ? "✓ " : "+ "}
                    {s}
                  </button>
                );
              })}
            </div>
          )}

          <TagsField
            label="Your skills"
            hint={
              form.categoryCode
                ? "Tap a suggestion above, or type your own and press Enter."
                : "Pick a category first to see suggestions — or type your own."
            }
            placeholder="Add a skill"
            value={form.skills ?? []}
            onChange={(sk) => set("skills", sk)}
          />
        </section>

        <div className="flex items-center gap-3">
          <Button type="submit" isLoading={busy}>
            {isEdit ? "Save changes" : "List my service"}
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={() =>
              navigate(isEdit ? `/lab/providers/${idOrSlug}` : "/lab/providers")
            }
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
}
